import { ExclamationTriangleIcon } from "@heroicons/react/20/solid";
import type { FunctionComponent } from "react";
import { NavLink } from "react-router-dom";

import Card from "~/components/Card/Card";
import HorizontalStats from "~/components/HorizontalStats/HorizontalStats";
import type { HorizontalStat } from "~/components/HorizontalStats/horizontalStatsTypes.ts";
import OverflowPercentageChart from "~/components/OverflowPercentageChart/OverflowPercentageChart";
import { formatCentsToCurrency } from "~/utils/currencyUtils.ts";

export type OverbookedOrderInfo = {
	orderId: string;
	linkPath: string;
	phaseNumber: number;
	orderTitle: string;
	centsOverbooked: number;
	percentageOverbookedFormatted: string;
};

type Props = {
	manDaysPlanned: number;
	manDaysTracked: number;
	budgetCentsPlanned: number;
	budgetCentsTracked: number;
	showBudget?: boolean;
	totalBudgetCents: number;
	totalManDays: number;
	overbookedOrders: OverbookedOrderInfo[];
};

const OverbookingWarnings: FunctionComponent<{ orders: OverbookedOrderInfo[] }> = ({ orders }) => {
	if (orders.length === 0) return null;

	const formatOverbooked = (order: OverbookedOrderInfo) =>
		`+${formatCentsToCurrency(order.centsOverbooked)}${order.percentageOverbookedFormatted && `  / ${order.percentageOverbookedFormatted}`}`;
	
	return (
		<div className="mt-6 flex flex-col gap-y-1 rounded-lg bg-yellow-50 px-4 py-2 text-yellow-800 shadow">
			<div className="flex items-center gap-x-2">
				<ExclamationTriangleIcon className="mt-0.5 size-5" />
				<span className="text-sm font-bold">
					{orders.length === 1 ? "Das Kostenvolumen folgender Bestellung ist überbucht:" : "Das Kostenvolumen folgender Bestellungen ist überbucht:"}
				</span>
			</div>
			<div className="flex flex-col gap-y-1 pl-7">
				{orders.map((order) => (
					<NavLink
						key={order.orderId}
						to={order.linkPath}
						className="text-sm hover:underline"
					>
						Phase {order.phaseNumber} – {order.orderTitle} ({formatOverbooked(order)})
					</NavLink>
				))}
			</div>
		</div>
	);
};

const ProjectBudgetSection: FunctionComponent<Props> = ({
	manDaysPlanned,
	manDaysTracked,
	budgetCentsPlanned,
	budgetCentsTracked,
	showBudget = false,
	overbookedOrders,
}) => {
	const plannedValue = showBudget ? budgetCentsPlanned : manDaysPlanned;
	const trackedValue = showBudget ? budgetCentsTracked : manDaysTracked;
	const percentageTracked = (trackedValue / plannedValue) * 100;

	const plannedValueFormatted = showBudget ? formatCentsToCurrency(plannedValue) : plannedValue.toFixed(0) + " PT";
	const trackedValueFormatted = showBudget ? formatCentsToCurrency(trackedValue) : trackedValue > 0 && trackedValue < 1 ? "<" : "" + +trackedValue.toFixed(0) + " PT";
	const restFormatted = showBudget ? formatCentsToCurrency(plannedValue - trackedValue) : (plannedValue - trackedValue).toFixed(0) + " PT";
	const percentageTrackedFormatted = percentageTracked === Infinity ? "n/a" : percentageTracked.toFixed(0) + "%";

	const stats: HorizontalStat[] = [{
		label: "Budget",
		value: plannedValueFormatted,
	},
		{
			label: "Verfügt",
			value: trackedValueFormatted,
		},
		{
			label: "Rest",
			value: restFormatted,
			isHighlighted: plannedValue - trackedValue < 0,
		}];

	return (
		<Card title="Budgetstatus">
			<div>
				<HorizontalStats stats={stats}>
					<OverflowPercentageChart
						percentage={percentageTracked === Infinity ? 0 : percentageTracked}
						size={80}
						thickness={0.4}
					>
						<div className="text-sm	 font-bold">{percentageTrackedFormatted}</div>
					</OverflowPercentageChart>
				</HorizontalStats>
			</div>
			<OverbookingWarnings orders={overbookedOrders} />
		</Card>
	);
};

export default ProjectBudgetSection;